import React, { useState, useCallback } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * useConfirm — replaces window.confirm() which is blocked on production sites.
 * Usage:
 *   const { confirm, ConfirmModal } = useConfirm();
 *   // In handler (must be async):
 *   const ok = await confirm("Delete this item?");
 *   if (!ok) return;
 *   // render: {ConfirmModal}
 */
export function useConfirm() {
  const [state, setState] = useState({ open: false, message: "", resolve: null });

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      setState({ open: true, message, resolve });
    });
  }, []);

  const handleYes = () => {
    state.resolve(true);
    setState({ open: false, message: "", resolve: null });
  };
  const handleNo = () => {
    state.resolve(false);
    setState({ open: false, message: "", resolve: null });
  };

  const ConfirmModal = state.open ? (
    <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-6 animate-fade-in-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#FBEAE7] flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-[#C2624E]" />
          </div>
          <p className="text-sm text-[#3A322C] font-medium">{state.message}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleNo}
            className="flex-1 border border-[#E5E1DB] rounded-xl py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleYes}
            className="flex-1 bg-[#C2624E] text-white rounded-xl py-2.5 text-sm font-bold hover:bg-[#a85040] transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, ConfirmModal };
}
